import { ExportConfig, ExportColumn, generateFileName } from './types';

/** Minimaler struktureller Input — BerichtZeile (lib/immobilien/bericht.ts) ist kompatibel. */
export interface MietRow {
  einheit?: {
    bezeichnung?: string | null; typ?: string | null;
    wohnflaeche?: number | null; etage?: string | null; status?: string | null;
  } | null;
  mieter?: {
    firma_name?: string | null; vorname?: string | null; nachname?: string | null;
    kaltmiete?: number | null; nebenkosten_vorauszahlung?: number | null; heizkosten?: number | null;
  } | null;
}

export interface ObjektInfo { name: string; objektnummer?: string | null; adresse?: string }

export interface MietKennzahlen {
  einheiten: number; vermietet: number; leer: number;
  summeKalt: number; summeWarm: number; jahresSoll: number;
}

const n0 = (v: number | null | undefined): number =>
  (typeof v === 'number' && !Number.isNaN(v) ? v : 0);

/** Warmmiete = Kalt + NK-Vorauszahlung + Heizkosten (fehlend = 0; ohne Mieter = 0). */
export function warmmiete(mieter: MietRow['mieter']): number {
  if (!mieter) return 0;
  return n0(mieter.kaltmiete) + n0(mieter.nebenkosten_vorauszahlung) + n0(mieter.heizkosten);
}

/** Anzeigename: Firma sonst "Vorname Nachname"; "Leerstand" wenn kein/leerer Mieter. */
export function mieterName(mieter: MietRow['mieter']): string {
  if (!mieter) return 'Leerstand';
  const firma = (mieter.firma_name || '').trim();
  if (firma) return firma;
  const name = `${mieter.vorname || ''} ${mieter.nachname || ''}`.trim();
  return name || 'Leerstand';
}

export function computeMietKennzahlen(rows: MietRow[]): MietKennzahlen {
  let vermietet = 0, summeKalt = 0, summeWarm = 0;
  for (const r of rows) {
    if (r.mieter) {
      vermietet += 1;
      summeKalt += n0(r.mieter.kaltmiete);
      summeWarm += warmmiete(r.mieter);
    }
  }
  return {
    einheiten: rows.length,
    vermietet,
    leer: rows.length - vermietet,
    summeKalt,
    summeWarm,
    jahresSoll: summeWarm * 12,
  };
}

/** Euro für Tabellenzellen: 0/fehlend → leere Zelle. */
const euroZelle = (v: number | null | undefined): string => {
  const x = n0(v);
  return x !== 0 ? x.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';
};

/** Euro für Kennzahlen-Box: immer mit Wert (auch 0,00). */
const euroLabel = (v: number): string =>
  v.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const UEBERSICHT_COLUMNS: ExportColumn[] = [
  { key: 'bezeichnung', label: 'Einheit', format: (_v, r: MietRow) => r.einheit?.bezeichnung || '' },
  { key: 'typ', label: 'Typ', format: (_v, r: MietRow) => r.einheit?.typ || '' },
  { key: 'wohnflaeche', label: 'Wohnfläche', format: (_v, r: MietRow) => r.einheit?.wohnflaeche != null ? `${r.einheit.wohnflaeche} m²` : '' },
  { key: 'mieter', label: 'Mieter', format: (_v, r: MietRow) => mieterName(r.mieter) },
  { key: 'kaltmiete', label: 'Kaltmiete (€)', format: (_v, r: MietRow) => euroZelle(r.mieter?.kaltmiete) },
  { key: 'nk', label: 'NK-Vorausz. (€)', format: (_v, r: MietRow) => euroZelle(r.mieter?.nebenkosten_vorauszahlung) },
  { key: 'heizkosten', label: 'Heizkosten (€)', format: (_v, r: MietRow) => euroZelle(r.mieter?.heizkosten) },
  { key: 'warm', label: 'Warmmiete (€)', format: (_v, r: MietRow) => euroZelle(warmmiete(r.mieter)) },
  { key: 'eurqm', label: '€/m²', format: (_v, r: MietRow) => {
      const fl = n0(r.einheit?.wohnflaeche); const kalt = n0(r.mieter?.kaltmiete);
      return fl > 0 && kalt > 0 ? (kalt / fl).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';
  } },
];

const AUFSTELLUNG_COLUMNS: ExportColumn[] = [
  { key: 'bezeichnung', label: 'Einheit', format: (_v, r: MietRow) => r.einheit?.bezeichnung || '' },
  { key: 'mieter', label: 'Mieter', format: (_v, r: MietRow) => mieterName(r.mieter) },
  { key: 'kaltmiete', label: 'Kaltmiete (€)', format: (_v, r: MietRow) => euroZelle(r.mieter?.kaltmiete) },
  { key: 'nk', label: 'NK (€)', format: (_v, r: MietRow) => euroZelle(r.mieter?.nebenkosten_vorauszahlung) },
  { key: 'warm', label: 'Warmmiete (€)', format: (_v, r: MietRow) => euroZelle(warmmiete(r.mieter)) },
];

export function buildMietuebersichtConfig(objekt: ObjektInfo, rows: MietRow[]): ExportConfig {
  const k = computeMietKennzahlen(rows);
  return {
    title: objekt.name,
    columns: UEBERSICHT_COLUMNS,
    data: rows,
    fileName: generateFileName(objekt.name.replace(/\s+/g, '_') + '_Mietuebersicht'),
    stats: {
      items: [
        { label: 'Einheiten', value: k.einheiten },
        { label: 'Vermietet', value: k.vermietet },
        { label: 'Leerstand', value: k.leer },
        { label: 'Kaltmiete/M (€)', value: euroLabel(k.summeKalt) },
        { label: 'Warmmiete/M (€)', value: euroLabel(k.summeWarm) },
        { label: 'Jahres-Soll (€)', value: euroLabel(k.jahresSoll) },
      ],
    },
  };
}

export function buildMietaufstellungConfig(objekt: ObjektInfo, rows: MietRow[]): ExportConfig {
  const vermietet = rows.filter((r) => r.mieter != null);
  // Kennzahlen über ALLE Einheiten (inkl. Leerstand); data nur vermietete
  const k = computeMietKennzahlen(rows);
  return {
    title: objekt.name,
    columns: AUFSTELLUNG_COLUMNS,
    data: vermietet,
    fileName: generateFileName(objekt.name.replace(/\s+/g, '_') + '_Mietaufstellung'),
    stats: {
      items: [
        { label: 'Mieter', value: k.vermietet },
        { label: 'Kaltmiete/M (€)', value: euroLabel(k.summeKalt) },
        { label: 'Warmmiete/M (€)', value: euroLabel(k.summeWarm) },
      ],
    },
  };
}
